/**
 * 인증 관련 유틸리티 함수들 (메인 엔트리 포인트)
 * 
 * 이 파일은 분리된 유틸리티 함수들을 re-export하는 중앙 집중식 엔트리 포인트입니다.
 * 모든 인증 관련 기능이 여러 작은 모듈로 분리되어 관리됩니다.
 */

import type { ITokenManager } from '@edusense/shared-types-auth';
import type { IIdeMessagingService } from '@edusense/shared-types-ide';
import type { AuthState } from '@edusense/shared-types-auth';
import type { User } from '@edusense/shared-types-user';
import type { ResolvedStorageKeys } from '../storage-keys';
import { resolveStorageKeys } from '../storage-keys';

// 분리된 유틸리티 함수들 import
import { clearAuthCookies } from './cookie-utils';
import { createLogoutHandler } from './logout-utils';
import { createFetchUserProfile, createFetchUserEmail } from './profile-utils';

/**
 * 인증 유틸리티 설정 인터페이스
 */
export interface AuthUtilsConfig {
  tokenManager: ITokenManager;
  apiService: any;
  isVSCodeEnv: boolean;
  ideMessengerInstance?: IIdeMessagingService | null;
  navigate: (path: string) => void;
  setAuthState: (state: AuthState | ((prev: AuthState) => AuthState)) => void;
  lastTokenRef: React.MutableRefObject<string | null>;
  storageKeys?: Partial<ResolvedStorageKeys>;
}

/**
 * 새로운 인증 시작 함수 생성
 * 기존 세션을 정리하고 새로운 인증 플로우를 시작합니다
 */
export const createStartFreshAuth = (config: AuthUtilsConfig) => () => {
  console.log('[AuthUtils] 새로운 인증 플로우 시작 - 기존 세션 쿠키 삭제');
  const storageKeys = resolveStorageKeys(config.storageKeys);
  
  if (!config.isVSCodeEnv) {
    // 기존 인증 상태 초기화
    config.setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null
    });
    
    // 토큰 정리
    config.tokenManager.setToken(null);
    config.apiService.setToken(null);
    config.lastTokenRef.current = null;
    
    // localStorage 정리
    localStorage.removeItem(storageKeys.tokenKey);
    localStorage.removeItem(storageKeys.userKey);
    localStorage.removeItem(storageKeys.refreshTokenKey);
    localStorage.removeItem(storageKeys.idTokenKey);
    
    // 기존 세션 쿠키 삭제
    clearAuthCookies();
  }
};

// 분리된 모듈에서 함수들을 re-export
export { clearAuthCookies } from './cookie-utils';
export { createLogoutHandler } from './logout-utils'; 
export { createFetchUserProfile, createFetchUserEmail } from './profile-utils';

/**
 * 모든 인증 유틸리티 함수들을 생성하는 팩토리 함수
 * 설정 객체를 받아서 모든 필요한 인증 관련 함수들을 반환합니다
 */
export const createAuthUtils = (config: AuthUtilsConfig) => ({
  // 로그아웃 관련
  logout: createLogoutHandler(config),
  
  // 프로필 관련
  fetchUserProfile: createFetchUserProfile(config),
  fetchUserEmail: createFetchUserEmail(config),
  
  // 세션 관리
  startFreshAuth: createStartFreshAuth(config),
  
  // 쿠키 관리
  clearAuthCookies
});

/**
 * 레거시 호환성을 위한 기본 export
 * 기존 코드가 개별 함수를 직접 import하는 경우를 지원합니다
 */
export default {
  createLogoutHandler,
  createFetchUserProfile, 
  createFetchUserEmail,
  createStartFreshAuth,
  clearAuthCookies,
  createAuthUtils
};
